/**
 * Errors signalling that the LLM passed invalid arguments to a tool.
 *
 * Recovery strategy: the LLM should retry the call with corrected
 * arguments based on the error's `suggestions` and `details`.
 */

import type { ExprError } from '#core/lib/expr/errors.ts';

import { type ErrorRecoveryStrategy, ToolExecutionError } from './base.ts';
import { toolError } from './registry.ts';

@toolError('InvalidToolInputError')
export abstract class InvalidToolInputError extends ToolExecutionError {
  readonly recovery: ErrorRecoveryStrategy = 'retry-with-different-input';
}

/**
 * ErrorTypes whose first `suggestions()` entry is the load-bearing recovery
 * hint — the "available columns / views / datasets" list. The compacted
 * failure line (`CompactToolFailureLine`) keeps that one suggestion for these
 * so a retry loop still sees the fix in the persistent transcript, not only on
 * the single turn the failure was the live result.
 */
export const IDENTIFIER_RESOLUTION_ERROR_TYPES: ReadonlySet<string> = new Set([
  'InvalidExpressionError',
  'ColumnNotFoundError',
  'ViewNotFoundError',
  'DatasetNotFoundError',
  'ViewsNotDescribedError',
]);
/**
 * Shared LLM-targeted suggestions for any error of the form "you referenced
 * an item by name; here are the names that exist." Used by every
 * {@link ItemNotFoundError} subclass and by call-site errors that mix this
 * list into a larger suggestion block (e.g. {@link ColumnNotFoundError},
 * {@link DerivedColumnAllNullError}).
 *
 * Pass `truncateAt` for item kinds whose available list can be very long
 * (e.g. View has thousands of entries). The omitted tail is summarised
 * inline (`… (+N more)`) so the LLM still sees the cardinality.
 */
export function availableItemsSuggestions(
  itemKind: string,
  available: readonly string[],
  opts: { truncateAt?: number } = {},
): readonly string[] {
  const { truncateAt = Number.POSITIVE_INFINITY } = opts;
  const list =
    available.length > truncateAt
      ? `${available.slice(0, truncateAt).join(', ')}, … (+${available.length - truncateAt} more)`
      : available.join(', ');
  return [`Available ${itemKind}s: ${list}.`, `${itemKind} names are case-sensitive.`];
}
/**
 * Shared base for "an item referenced by name was not found" errors.
 *
 * Subclasses carry their `@toolError` discriminator (the wire format,
 * matching the un-minified class name) and pass an `itemKind` to the
 * constructor; the base composes the `${itemKind} "${requested}" not found.`
 * message and the `availableItemsSuggestions(itemKind, available)` recovery
 * hints. Override `emptyStateSuggestions` for kind-specific guidance when
 * the available list is empty (e.g. "no datasets are registered yet").
 *
 * Subclasses with bespoke message wording (e.g.
 * {@link SourceRefColumnMissingError}) pass `messageOverride` to bypass
 * the canonical template while still inheriting the suggestion shape.
 *
 * Subclasses with very long available lists (e.g. {@link ViewNotFoundError})
 * set `protected override readonly truncateAt = N` to limit the inline
 * preview; the omitted tail is summarised as `… (+N more)`.
 */
@toolError('ItemNotFoundError')
export abstract class ItemNotFoundError extends InvalidToolInputError {
  /** Maximum items rendered inline in the suggestions preview. Defaults to no limit. */
  protected readonly truncateAt: number = Number.POSITIVE_INFINITY;

  constructor(
    readonly itemKind: string,
    readonly requested: string,
    readonly available: readonly string[],
    messageOverride?: string,
  ) {
    super(messageOverride ?? `${itemKind} "${requested}" not found.`);
  }

  /**
   * Override per subclass when the available list is empty and the
   * generic "No {kind}s available." doesn't carry enough context for
   * the LLM (e.g. "No datasets are registered yet — call a producer
   * tool first").
   */
  protected emptyStateSuggestions(): readonly string[] {
    return [`No ${this.itemKind.toLowerCase()}s available.`];
  }

  override suggestions(): readonly string[] {
    if (this.available.length === 0) {
      return this.emptyStateSuggestions();
    }
    return availableItemsSuggestions(this.itemKind, this.available, { truncateAt: this.truncateAt });
  }
}

/**
 * A dataset (by name or by external ID) referenced in tool input was not
 * registered by any earlier tool call.
 */
@toolError('DatasetNotFoundError')
export class DatasetNotFoundError extends ItemNotFoundError {
  constructor(requested: string, available: readonly string[]) {
    super('Dataset', requested, available);
  }

  protected override emptyStateSuggestions(): readonly string[] {
    return [
      'No datasets are registered yet. Call a producer tool first (e.g. queryKnowledgeGraph, fetchTimeseriesDatapoints, registerDataset).',
    ];
  }
}

/** A view (data-modeling view) referenced in tool input was not found. */
@toolError('ViewNotFoundError')
export class ViewNotFoundError extends ItemNotFoundError {
  protected override readonly truncateAt = 20;

  constructor(requested: string, available: readonly string[]) {
    super('View', requested, available);
  }

  protected override emptyStateSuggestions(): readonly string[] {
    return ['No views are configured for this workspace.'];
  }
}
/**
 * The agent issued a `queryKnowledgeGraph` against a view it never described.
 * `describeViews` must precede a query so column names are referenced from the
 * real schema rather than guessed; this guard hard-denies the premature query
 * and embeds the missing view schema so the retry is informed in one step.
 */
@toolError('ViewsNotDescribedError')
export class ViewsNotDescribedError extends InvalidToolInputError {
  constructor(
    readonly undescribedViews: readonly string[],
    /** Markdown schema for the undescribed views, embedded in the message so the
     * agent can correct column names without a separate round trip. */
    readonly schemaMarkdown: string,
  ) {
    const list = undescribedViews.map((view) => `"${view}"`).join(', ');
    const subject = undescribedViews.length === 1 ? 'this view' : 'these views';
    super(
      `Call \`describeViews\` before querying ${subject}: ${list}. You must call this tool to describe all views you intend to query before executing the query.

Call \`describeViews\` with the undescribed view names now, then proceed with your task.`,
    );
  }

  override suggestions(): readonly string[] {
    const list = this.undescribedViews.map((view) => `"${view}"`).join(', ');
    return [
      `Call describeViews({ views: [${list}] }) to see the full rendered schema, then retry the query using the exact column names it lists.`,
    ];
  }

  // Keep the embedded schema out of `details` — it already lives in the message.
  override details() {
    return { undescribedViews: this.undescribedViews };
  }
}

/**
 * One or more column names referenced in tool input do not exist on the
 * input dataset. Raised by select / sort / groupBy / pivot / fold / spread /
 * unroll / unnest / join operators.
 *
 * The optional `operationHint` carries an operator-specific explanation
 * that's prepended to the standard suggestions. Useful when the column
 * absence has a non-obvious cause — e.g. a reshape operator earlier in
 * the pipeline dropped it, and the bare "available columns" list would
 * mislead the agent into thinking it was a typo.
 */
@toolError('ColumnNotFoundError')
export class ColumnNotFoundError extends InvalidToolInputError {
  constructor(
    /** Operator that was being executed (e.g. `select`, `sort`, `join`, `unnest`). */
    readonly operation: string,
    readonly missingColumns: readonly string[],
    readonly availableColumns: readonly string[],
    readonly operationHint?: string,
  ) {
    const plural = missingColumns.length === 1 ? '' : 's';
    super(`${operation}: column${plural} not found: ${missingColumns.map((c) => `"${c}"`).join(', ')}.`);
  }

  override suggestions(): readonly string[] {
    const hint = this.operationHint === undefined ? [] : [this.operationHint];
    return [
      ...hint,
      ...availableItemsSuggestions('Column', this.availableColumns),
      'For nested fields, use dot notation (e.g. `metadata.unit`). Bracket access (`row["foo.bar"]`) is equivalent — both look up the literal property `foo.bar` on the row, not a path traversal.',
    ];
  }

  override details() {
    return {
      operation: this.operation,
      missingColumns: this.missingColumns,
      availableColumns: this.availableColumns,
      ...(this.operationHint === undefined ? {} : { operationHint: this.operationHint }),
    };
  }
}
/**
 * A derive expression evaluated to null for every row of a non-trivial input.
 * Two failure shapes share this error so the agent sees one consistent
 * signal whenever a derive's output column is unusable:
 *
 *  - **Silent-undefined** (no per-row errors collected) — the expression
 *    resolved cleanly per row but to undefined. Canonical cases:
 *    - `row["wellString.externalId"]` when the row has `wellString` (an
 *      object) rather than a flat property of that name.
 *    - A timeseries fetcher that returned empty datapoints for every
 *      externalId, then `last([])` / `value(undefined)` cascading to null.
 *    - An array reducer over an array intermediate that was empty in every
 *      row.
 *
 *  - **Per-row evaluation errors throughout** (`sampleErrors` populated) —
 *    every row's evaluation threw the same kind of error (e.g.
 *    `sumRunning is not a function`, `strlen expected a string, got
 *    number`). Per-row errors are otherwise collected and downgraded to a
 *    "N rows failed" warning that the tool layer drops; surfacing them
 *    here makes the actual underlying message visible to the agent.
 *
 * Distinct from {@link ColumnNotFoundError}: that fires at validation time
 * when a column reference is statically unresolvable. This fires at
 * post-evaluation time when references resolved fine but the evaluation
 * itself yielded only nulls.
 */
@toolError('DerivedColumnAllNullError')
export class DerivedColumnAllNullError extends InvalidToolInputError {
  constructor(
    readonly deriveName: string,
    readonly expression: string,
    readonly rowCount: number,
    readonly availableColumns: readonly string[],
    readonly referencedColumns: readonly string[],
    /**
     * Up to `MAX_SAMPLE_EVAL_ERRORS` per-row evaluation errors collected
     * before this throw. Empty when the failure was silent-undefined.
     */
    readonly sampleErrors: readonly { row: number; error: string }[] = [],
  ) {
    const errBlock =
      sampleErrors.length > 0
        ? ` Per-row evaluation errors (${sampleErrors.length} sampled): ${sampleErrors
            .map((e) => `row ${e.row}: ${e.error}`)
            .join('; ')}.`
        : ' (No per-row errors collected — the expression silently produced undefined.)';
    super(`derive: column "${deriveName}" evaluated to null for all ${rowCount} rows.${errBlock}`);
  }

  override suggestions(): readonly string[] {
    const refList =
      this.referencedColumns.length > 0 ? this.referencedColumns.map((c) => `"${c}"`).join(', ') : '(none)';
    const out: string[] = [`Expression: \`${this.expression}\`.`];
    if (this.sampleErrors.length > 0) {
      out.push(
        'The expression threw the same error on every row — the message above shows what went wrong (function name typo, type mismatch, missing column on the row value, etc.). Fix the expression and retry.',
      );
    } else {
      out.push(
        'The expression resolved without throwing but produced undefined for every row — check that the referenced columns carry non-null values and that any fetchers / array reducers return non-empty data.',
      );
    }
    out.push(
      `Columns referenced by the expression: ${refList}.`,
      'For `reference<X>` columns (e.g. `wellString`), use member access `wellString.externalId` — `row["wellString.externalId"]` is equivalent to `row.wellString.externalId` (a literal-key lookup), not a path traversal.',
      'For timeseries fetchers (`datapoints`, `hourly`, `daily`, `weekly`, `monthly`), confirm the referenced column is a `timeseries<number><CogniteTimeSeries>` and that the externalIds resolve to series with datapoints in CDF.',
      ...availableItemsSuggestions('Column', this.availableColumns),
    );
    return out;
  }
}
/**
 * A derive expression failed to parse — `math.compile` threw before any
 * row was evaluated. Distinct from {@link DerivedColumnAllNullError},
 * which fires post-evaluation when references resolved fine but every
 * row's value collapsed to null. This error fires strictly earlier, at
 * compile time, before any per-row scope is built.
 *
 * Raised directly rather than swallowed: dropping the column and pushing
 * an "Invalid expression" warning would let downstream operators fail
 * later with a confusing {@link ColumnNotFoundError} that hides the actual
 * parse error and the offending expression. Surfacing the parse failure
 * here gives the agent the math.js error message and the original
 * expression so it can fix the syntax and retry.
 *
 * `syntaxHints` is a stable-ordered list of actionable diagnoses produced
 * by a JavaScript-syntax-tell detector (arrow functions, `const`/`let`,
 * `return`, `if`-statements, `obj.key` property access, `===`/`!==`).
 * Empty by default so this class can be raised before the detector is
 * wired in.
 */
@toolError('DerivedColumnCompileError')
export class DerivedColumnCompileError extends InvalidToolInputError {
  constructor(
    readonly deriveName: string,
    readonly expression: string,
    readonly parseError: string,
    readonly availableColumns: readonly string[],
    readonly syntaxHints: readonly string[] = [],
  ) {
    super(`derive: failed to compile expression for column "${deriveName}": ${parseError}`);
  }

  override suggestions(): readonly string[] {
    const out: string[] = [`Expression: \`${this.expression}\`.`];
    if (this.syntaxHints.length > 0) {
      out.push(...this.syntaxHints);
    }
    out.push(
      'Math.js callbacks use `f(x, i) = expr` (a single expression, no `=>` and no block body).',
      'Property access on objects is `prop(obj, "key")` — the `.` operator is matrix dot product.',
      'Use `value(dp)` to extract the scalar value from a datapoint.',
      'Conditionals use the ternary `cond ? a : b`; math.js has no if-statement.',
      ...availableItemsSuggestions('Column', this.availableColumns),
    );
    return out;
  }
}

/**
 * A column reference is structurally invalid (wrong type, empty, conflicting,
 * or used with an incompatible operator). Distinct from `ColumnNotFoundError`
 * in that the column may exist — but the way it's referenced is wrong.
 */
@toolError('InvalidColumnReferenceError')
export class InvalidColumnReferenceError extends InvalidToolInputError {
  constructor(
    message: string,
    /** Column that was referenced. */
    readonly column: string,
    /** Optional one-line hint pointing to the fix. */
    readonly hint?: string,
  ) {
    super(message);
  }

  override suggestions(): readonly string[] {
    return this.hint ? [this.hint] : [];
  }

  override details() {
    return { column: this.column };
  }
}

/** A `limit` value passed to an operator is out of range or wrong type. */
@toolError('InvalidLimitError')
export class InvalidLimitError extends InvalidToolInputError {
  constructor(
    readonly limit: number,
    readonly reason: string,
  ) {
    super(`Invalid limit value: ${limit}. ${reason}`);
  }

  override suggestions(): readonly string[] {
    return ['Use a positive integer between 1 and 10000.'];
  }

  override details() {
    return { limit: this.limit };
  }
}

/**
 * The tool input was syntactically valid Zod-wise, but semantically incomplete
 * or contradictory (e.g. dashboard spec missing required panels, a `pivot`
 * spec missing `:cols`, missing outputDataset key, etc.).
 *
 * Use this when the failure is a one-off spec validation that doesn't warrant
 * its own dedicated class. Pass tailored suggestions and details from the
 * call site.
 */
@toolError('InvalidSpecError')
export class InvalidSpecError extends InvalidToolInputError {
  readonly #suggestions: readonly string[];
  readonly #details: Readonly<Record<string, unknown>>;

  constructor(
    message: string,
    opts: { suggestions?: readonly string[]; details?: Readonly<Record<string, unknown>> } = {},
  ) {
    super(message);
    this.#suggestions = opts.suggestions ?? [];
    this.#details = opts.details ?? {};
  }

  override suggestions(): readonly string[] {
    return this.#suggestions;
  }

  override details() {
    return this.#details;
  }
}

// Source-reference filter errors (knowledge-graph queries).

/** A source-reference filter referenced a column that doesn't exist on the input. */
@toolError('SourceRefColumnMissingError')
export class SourceRefColumnMissingError extends ItemNotFoundError {
  constructor(column: string, available: readonly string[]) {
    super('Column', column, available, `Source-reference filter references unknown column "${column}".`);
  }
}

/** A source-reference filter referenced a column whose type is incompatible. */
@toolError('SourceRefTypeMismatchError')
export class SourceRefTypeMismatchError extends InvalidToolInputError {
  constructor(
    readonly column: string,
    readonly expectedType: string,
    readonly actualType: string,
  ) {
    super(`Source-reference filter on column "${column}" expects type ${expectedType} but column is ${actualType}.`);
  }

  override suggestions(): readonly string[] {
    return [
      'Source-reference filters require columns of type "reference" or "directRelation".',
      'Use a different column or transform the source dataset to expose a reference column.',
    ];
  }
}

/** A source-reference filter resolved to an empty set of source rows. */
@toolError('SourceRefEmptyError')
export class SourceRefEmptyError extends InvalidToolInputError {
  constructor(readonly column: string) {
    super(`Source-reference filter on column "${column}" yielded no rows from the source dataset.`);
  }

  override suggestions(): readonly string[] {
    return [
      'Verify the source dataset is non-empty.',
      'Verify upstream filters did not remove all rows before the source-reference filter ran.',
    ];
  }
}

/** A source-reference filter used an operator that is not supported in this position. */
@toolError('SourceRefInvalidOpError')
export class SourceRefInvalidOpError extends InvalidToolInputError {
  constructor(
    readonly operator: string,
    readonly allowedOperators: readonly string[],
  ) {
    super(`Operator "${operator}" is not allowed in a source-reference filter.`);
  }

  override suggestions(): readonly string[] {
    return [`Allowed operators: ${this.allowedOperators.join(', ')}.`];
  }
}

/**
 * Two or more source-reference filters target the same column with conflicting
 * specs (e.g. one expects strings, another expects directRelations).
 */
@toolError('SourceRefConflictError')
export class SourceRefConflictError extends InvalidToolInputError {
  constructor(
    readonly column: string,
    readonly conflictDescription: string,
  ) {
    super(`Source-reference filter conflict on column "${column}": ${conflictDescription}.`);
  }

  override suggestions(): readonly string[] {
    return ['Remove one of the conflicting filters or harmonize their specs.'];
  }
}
/**
 * The LLM passed an s-expression the engine rejected at parse or eval
 * time (any `ExprError` — unclosed list, unknown column/op, type
 * mismatch, …). Retryable: the model should correct the expression and
 * call again. Delegates `message` / `suggestions` / `details` to the
 * underlying engine error so the recovery hints it already computes
 * (did-you-mean lists, position info) reach the LLM verbatim.
 */
@toolError('InvalidExpressionError')
export class InvalidExpressionError extends InvalidToolInputError {
  constructor(readonly exprError: ExprError) {
    // Surface the source position so the LLM can locate the offending
    // node (`3:7: expected an expression…`) instead of a position-less
    // message it cannot act on. `format()` always prefixes, so guard on
    // a real position — `NO_POS` (line 0) would add a useless `0:0:`.
    super(exprError.pos.line > 0 ? exprError.format() : exprError.message);
  }

  override suggestions(): readonly string[] {
    return this.exprError.suggestions();
  }

  override details() {
    return this.exprError.details();
  }
}
